import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { Novedad, NovedadesService } from "@services/NovedadesService";
import { NovedadesExportService } from "@services/NovedadesExportService";
import { HollowedButton, IconButton, NormalButton } from "@shared/designSystem/Button";
import {
    HorizontalContainer,
    SimpleCardContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import { SelectedSearchInput } from "@shared/designSystem/SelectedSearchInput";
import { TextInput } from "@shared/designSystem/TextInput";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

import {
    StatusMappingEditableGroup,
    useExportNovedadesStatusMappingConfigurationViewModel,
} from "./ExportNovedadesStatusMappingConfigurationViewModel";

interface ExportNovedadesStatusMappingConfigurationScreenProps {
    orgID: string;
    exportService: NovedadesExportService;
    novedadesService: NovedadesService;
}

const renderNovedadResult = (novedad: Novedad) => {
    return (
        <VerticalContainer gap={designSystemStyles.gapNone} margin="0">
            <Text text={`${novedad.acronym} - ${novedad.name}`} truncate={true} />
            {novedad.payrollCode && (
                <SmallText
                    text={`${"Código nómina".localized()}: ${novedad.payrollCode}`}
                    color={designSystemStyles.colorTextSecondary}
                    truncate={true}
                />
            )}
        </VerticalContainer>
    );
};

const groupDescription = (group: StatusMappingEditableGroup): string => {
    if (group.builtIn) {
        return `${"Columna del reporte".localized()}: ${group.totalsField}`;
    }

    return `${"Columna personalizada".localized()}: ${group.totalsField}`;
};

const ExportNovedadesStatusMappingConfigurationScreen: React.FC<ExportNovedadesStatusMappingConfigurationScreenProps> = ({
    orgID,
    exportService,
    novedadesService,
}) => {
    const viewModel = useExportNovedadesStatusMappingConfigurationViewModel({
        orgID,
        exportService,
        novedadesService,
    });

    const handleSave = async () => {
        const didSave = await viewModel.save();
        if (didSave) {
            ToastManager.shared().showSuccessMessage("Configuración guardada.".localized());
        } else {
            ToastManager.shared().showErrorMessage("No se pudo guardar la configuración.".localized());
        }
    };

    if (viewModel.loading) {
        return (
            <VerticalContainer height="100%" alignment="center" justifyContent="center">
                <QontyLoading text={"Cargando configuración...".localized()} />
            </VerticalContainer>
        );
    }

    if (viewModel.error && viewModel.groups.length === 0) {
        return (
            <VerticalContainer height="100%" alignment="center" justifyContent="center">
                <QontyEmptyState text={viewModel.error} darkMode={false} />
            </VerticalContainer>
        );
    }

    return (
        <VerticalContainer
            width="100%"
            height="100%"
            gap={designSystemStyles.gapLg}
            alignment="stretch"
        >
            <VerticalContainer gap={designSystemStyles.gapSm} margin="0">
                <Text
                    text={"Agrupá novedades para contar columnas del reporte y conceptos de exportación.".localized()}
                    color={designSystemStyles.colorText}
                />
            </VerticalContainer>

            <div style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: designSystemStyles.paddingSm,
            }}>
                <VerticalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    margin="0"
                    alignment="stretch"
                >
                    {viewModel.groups.map((group) => (
                        <SimpleCardContainer
                            key={group.totalsField}
                            padding={designSystemStyles.paddingMd}
                            borderRadius={designSystemStyles.radiusItem}
                            backgroundColor={designSystemStyles.colorBackgroundWhite}
                        >
                            <VerticalContainer gap={designSystemStyles.gapSm} margin="0">
                                <HorizontalContainer width="100%" justifyContent="space-between" alignment="flex-start">
                                    <VerticalContainer gap={designSystemStyles.gapXxxSm} margin="0" minWidth="0">
                                        <Text text={`**${group.label}**`} markdown={true} truncate={true} />
                                        <SmallText
                                            text={groupDescription(group)}
                                            color={designSystemStyles.colorTextSecondary}
                                            truncate={true}
                                        />
                                    </VerticalContainer>
                                    {!group.builtIn && (
                                        <IconButton
                                            iconType="delete"
                                            tooltip={"Eliminar columna".localized()}
                                            onClick={() => viewModel.removeGroup(group.totalsField)}
                                            disabled={viewModel.saving}
                                        />
                                    )}
                                </HorizontalContainer>

                                <SelectedSearchInput<Novedad>
                                    value={viewModel.selectedNovedadesForGroup(group)}
                                    results={viewModel.resultsForGroup(group)}
                                    query={group.query}
                                    onQueryChange={(query) => viewModel.setGroupQuery(group.totalsField, query)}
                                    onDebouncedQueryChange={(query) => viewModel.setGroupDebouncedQuery(group.totalsField, query)}
                                    onSelect={(novedad) => viewModel.addNovedadToGroup(group.totalsField, novedad)}
                                    onRemove={(novedad) => viewModel.removeNovedadFromGroup(group.totalsField, novedad)}
                                    onEnterPress={() => {}}
                                    getItemKey={(novedad) => novedad.acronym}
                                    getItemLabel={(novedad) => novedad.acronym}
                                    renderResult={renderNovedadResult}
                                    placeholder={"Buscar novedad".localized()}
                                    disabled={viewModel.saving}
                                    maxVisibleResults={4}
                                />
                            </VerticalContainer>
                        </SimpleCardContainer>
                    ))}

                    <HorizontalContainer width="100%" gap={designSystemStyles.gapMd} alignment="flex-end">
                        <TextInput
                            label={"Nueva columna".localized()}
                            placeholder={"Ej. Llegadas tarde".localized()}
                            value={viewModel.newColumnLabel}
                            onChange={viewModel.setNewColumnLabel}
                            onEnterPress={viewModel.createColumn}
                            disabled={viewModel.saving}
                        />
                        <NormalButton
                            text={"Agregar".localized()}
                            onClick={viewModel.createColumn}
                            disabled={!viewModel.canCreateColumn}
                            width="140px"
                        />
                    </HorizontalContainer>

                    {viewModel.error && (
                        <Text text={viewModel.error} color={designSystemStyles.colorError} />
                    )}
                </VerticalContainer>
            </div>

            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapMd}>
                <HollowedButton
                    text={"Cancelar".localized()}
                    onClick={viewModel.cancel}
                    disabled={!viewModel.hasChanges || viewModel.saving}
                    width="160px"
                />
                <NormalButton
                    text={viewModel.saving ? "Guardando...".localized() : "Guardar".localized()}
                    onClick={() => void handleSave()}
                    disabled={!viewModel.hasChanges || viewModel.saving}
                    loading={viewModel.saving}
                    width="160px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default ExportNovedadesStatusMappingConfigurationScreen;
